import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { registerService } from '@shared/services/register';

const REPORT_PAGE_TITLE = 'Приложение №1 към чл. 8, т. 1';

// Същите редове като в ElectronicWork / PDF – fieldKey/hardcoded за мапване от response
const REPORT_ROWS = [
    { fieldKey: '', hardcoded: '01' },
    { fieldKey: 'noiCode' },
    { fieldKey: 'typeDoc' },
    { fieldKey: 'bulstat' },
    { fieldKey: 'idn' },
    { fieldKey: 'identType' },
    { fieldKey: 'firstName' },
    { fieldKey: 'fatherName' },
    { fieldKey: 'surName' },
    { fieldKey: 'contractDate' },
    { fieldKey: 'contractNo' },
    { fieldKey: 'reasonCode' },
    { fieldKey: '' },
    { fieldKey: 'effectiveDate' },
    { fieldKey: 'validToDate' },
    { fieldKey: '' },
    { fieldKey: '' },
    { fieldKey: '' },
    { fieldKey: 'salary' },
    { fieldKey: 'nkpdCode' },
    { fieldKey: 'positionCode' },
    { fieldKey: 'nkidCode' },
    { fieldKey: 'ekkateCode' },
    { fieldKey: 'workTimeCode' },
    { fieldKey: 'durationDays' },
    { fieldKey: 'daysAgreedPaidAnnualLeave' },
    { fieldKey: 'usedVacationDays' },
    { fieldKey: 'breakDateContract' },
    { fieldKey: 'leaveLawReason' },
    { fieldKey: 'datePaidCompensation222_2' },
    { fieldKey: 'datePaidCompensation222_3' },
    { fieldKey: 'type0WorkЕxperience' },
    { fieldKey: 'unrecognizedТype0WorkЕxperienceNot' },
    { fieldKey: 'specialTypeOfWorkЕxperience' },
    { fieldKey: 'blockedSalaryAmount' }
];

// Ред на полетата в таблицата и брой квадратчета: [ rowIndex в REPORT_ROWS, count ]
const SLOTS = [
    [0, 2], [1, 1], [2, 1], [3, 13], [4, 10], [5, 1],
    [9, 8], [11, 3], [12, 3], [13, 8], [14, 8], [15, 8], [16, 8],
    [18, 15], [19, 8], [20, 12], [21, 4], [22, 5], [23, 1], [24, 2],  // 23 = workTimeCode, 24 = durationDays (23A)
    [25, 3], [26, 4], [27, 8], [28, 3], [29, 8], [30, 8],
    [31, 6], [32, 6], [33, 6], [34, 1],
];

const normalizeToRecords = (data) => {
    if (data == null) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.content)) return data.content;
    if (Array.isArray(data.data)) return data.data;
    if (typeof data === 'object' && !Array.isArray(data)) return [data];
    return [];
};

function getRecordValue(record, fieldKey) {
    if (record == null || !fieldKey) return '';
    if (record[fieldKey] != null) return String(record[fieldKey]);
    const snake = fieldKey.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
    return record[snake] != null ? String(record[snake]) : '';
}

function getRowValue(record, row) {
    if (row.hardcoded != null) return String(row.hardcoded);
    const raw = getRecordValue(record, row.fieldKey);
    // Запорни съобщения (blockedSalaryAmount): от API true -> "1", false -> "0"
    if (row.fieldKey === 'blockedSalaryAmount') {
        const val = record?.[row.fieldKey] ?? record?.blocked_salary_amount;
        if (typeof val === 'boolean') return val ? '1' : '0';
        if (raw === 'true') return '1';
        if (raw === 'false') return '0';
    }
    return raw;
}

// Квадратчета за един слот: value се показва символ по символ, винаги count на брой полета
function SlotInputs({ value, count }) {
    const s = String(value || '').slice(0, count);
    const chars = [...s];
    return (
        <>
            {Array.from({ length: count }, (_, i) => (
                <input key={i} className="nra-square" value={chars[i] ?? ''} readOnly />
            ))}
        </>
    );
}

export default function NraReport() {
    const [searchParams] = useSearchParams();
    const [records, setRecords] = useState([]);

    const parId = searchParams.get('parId') || undefined;

    // Set a meaningful document title while on the report page
    useEffect(() => {
        const previousTitle = document.title;
        document.title = REPORT_PAGE_TITLE;
        return () => { document.title = previousTitle; };
    }, []);

    // Fetch records for the given parId and decide how many forms (tables) to render.
    useEffect(() => {
        if (!parId) return;

        let cancelled = false;

        const fetchData = async () => {
            try {
                const res = await registerService.getEmpContractsParId(parId);
                const normalized = normalizeToRecords(res);
                if (!cancelled) {
                    setRecords(normalized);
                }
            } catch (error) {
                console.error('Error fetching NRA report data', error);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [parId]);

    // If there are no records, still render a single empty form
    const forms = records.length > 0 ? records : [null];

    return (
        <div className="nra-report-page">
            {forms.map((record, index) => {
                const slotValues = SLOTS.map(([ri, c]) => getRowValue(record, REPORT_ROWS[ri]));
                return (
                    <div
                        key={index}
                        className="nra-report"
                        style={index < forms.length - 1 ? { pageBreakAfter: 'always' } : undefined}
                    >
                        <table className="report-table">
                            <tbody className="flex-column">
                                <tr className="tr-row-wrapper">
                                    <td className="nra-report-title-cell">Електронен трудов запис за сключване, изменение и прекратяване на трудов договор и за възникване, промяна и прекратяване на служебно правоотношение</td>
                                </tr>
                                <tr className="tr-row-wrapper justify-content-center">
                                    <tr className="tr-section-divider">
                                        <td>1. Вид правоотношение</td>
                                        <td className='no-wrap'>
                                            <SlotInputs value={slotValues[0]} count={SLOTS[0][1]} />
                                        </td>
                                    </tr>
                                    <tr className="tr-section-divider">
                                        <td>2. Код корекция</td>
                                        <td className='no-wrap'>
                                            <SlotInputs value={slotValues[1]} count={SLOTS[1][1]} />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>3. Тип на документа</td>
                                        <td>
                                            <SlotInputs value={slotValues[2]} count={SLOTS[2][1]} />
                                        </td>
                                    </tr>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td>4. ЕИК на работодателя / органа по назначаването</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[3]} count={SLOTS[3][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="tr-section-divider" >Данни за лицето</td>
                                    <td>5. ЕГН/ЛН,ЛНЧ/ Сл.номер от НАП</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[4]} count={SLOTS[4][1]} /></td>
                                    <td>6. Код за ЕГН /ЛН, ЛНЧ/ Сл. номер от НАП</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[5]} count={SLOTS[5][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="no-wrap width-110">7. Име</td>
                                    <td className='row-section-divider-left'>{getRecordValue(record, 'firstName')}</td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="no-wrap width-110">8. Презиме</td>
                                    <td className='row-section-divider-left'>{getRecordValue(record, 'fatherName')}</td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="no-wrap width-110">9. Фамилия</td>
                                    <td className='row-section-divider-left'>{getRecordValue(record, 'surName')}</td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">10. Дата на сключване на трудовия договор или на акта за назначаване</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[6]} count={SLOTS[6][1]} /></td>
                                    <td className="tr-section-divider">11. Номер на акта за назначаване</td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">12. Основание на трудовия договор/акта за назначаване или основание за промяна на правоотношението</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[7]} count={SLOTS[7][1]} /></td>
                                    <td>13. Ранг на държавния служител(дипломатически ранг)</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[8]} count={SLOTS[8][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">14. Дата на начало на изпълнение на трудовия договор или на възникване на служебното правоотношение</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[9]} count={SLOTS[9][1]} /></td>
                                    <td className="nra-label-wrap">15. Срок на договора (на допълнителното споразумение) или на акта за назначаване</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[10]} count={SLOTS[10][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td>16. Дата на влизане в сила на изменението на трудовото или служебното правоотношение</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[11]} count={SLOTS[11][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">16А. Дата на влизане в сила на изменението на трудовото или служебното правоотношение</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[12]} count={SLOTS[12][1]} /></td>
                                    <td className="nra-label-wrap tr-section-divider">17. Номер на акта за промяна на служебното правоотношение</td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">18. Размер на основното трудово възнаграждение или на основната месечна заплата</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[13]} count={SLOTS[13][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className>19. Код по НКПД</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[14]} count={SLOTS[14][1]} /></td>
                                    <td className="nra-label-wrap tr-section-divider">20. Код на заеманата длъжност от държавния служител</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[15]} count={SLOTS[15][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td>21. Код по КИД</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[16]} count={SLOTS[16][1]} /></td>
                                    <td>22. Код по ЕКАТТЕ</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[17]} count={SLOTS[17][1]} /></td>
                                    <td>23. Код за работно време</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[18]} count={SLOTS[18][1]} /></td>
                                    <td className="nra-label-wrap">23A. Продължителност на работното време</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[19]} count={SLOTS[19][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="">24. Дни уговорен платен годишен отпуск по трудовото правоотношение</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[20]} count={SLOTS[20][1]} /></td>
                                    <td className="nra-label-wrap">25. Дни използван платен годишен отпуск полагащ се за годината на прекратяване на трудовото правоотношение</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[21]} count={SLOTS[21][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="">26. Дата на прекратяване на правоотношението</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[22]} count={SLOTS[22][1]} /></td>
                                    <td>27. Основание за прекратяване</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[23]} count={SLOTS[23][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td>28. Изплатено обезщетение по чл. 222, ал. 2 от КТ при прекратяване на трудовия договор (дата)</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[24]} count={SLOTS[24][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td>29. Изплатено обезщетение по чл. 222, ал. 2 от КТ при прекратяване на трудовия договор (сума)</td>
                                    <td className='no-wrap'>
                                        <SlotInputs value={slotValues[25]} count={SLOTS[25][1]} /></td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">30. Продължителност на времето, което се признава за трудов стаж</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <div className="nra-date-inputs">
                                            {[['yy', 0], ['yy', 1], ['mm', 2], ['mm', 3], ['dd', 4], ['dd', 5]].map(([lbl, i]) => (
                                                <div key={i} className="nra-date-group"><span className="nra-date-label">{lbl}</span><input className="nra-square" value={(slotValues[26] || '')[i] ?? ''} readOnly /></div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="nra-label-wrap">31. Продължителност на времето, което НЕ се признава за трудов стаж</td>
                                    <td className='no-wrap'>
                                        <div className="nra-date-inputs">
                                            {[['yy', 0], ['yy', 1], ['mm', 2], ['mm', 3], ['dd', 4], ['dd', 5]].map(([lbl, i]) => (
                                                <div key={i} className="nra-date-group"><span className="nra-date-label">{lbl}</span><input className="nra-square" value={(slotValues[27] || '')[i] ?? ''} readOnly /></div>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr className="tr-row-wrapper">
                                    <td className="nra-label-wrap">32. Продължителност на времето, което се признава за служебен стаж или за стаж в дипломатическата служба</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <div className="nra-date-inputs">
                                            {[['yy', 0], ['yy', 1], ['mm', 2], ['mm', 3], ['dd', 4], ['dd', 5]].map(([lbl, i]) => (
                                                <div key={i} className="nra-date-group"><span className="nra-date-label">{lbl}</span><input className="nra-square" value={(slotValues[28] || '')[i] ?? ''} readOnly /></div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="nra-label-wrap">33. Запорни съобщения</td>
                                    <td className='no-wrap tr-section-divider'>
                                        <SlotInputs value={slotValues[29]} count={SLOTS[29][1]} /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div >
                );
            })}
        </div>
    );
}